import type { AppState } from '@/store/rootTypes'
import type { StudentProfile } from '@/types'
import type { StudentAccount, StudentWorkspace } from '@/entities/student-workspace'
import { migrateStudentLearningProfile } from '@/entities/profile/migration'
import { getPreferredLearningStyle } from '@/entities/profile/getPreferredLearningStyle'
import { getPreferredLearnerType } from '@/entities/profile/getSocialLearningProfile'
import { LocalStorageService, STORAGE_KEYS } from '@/services/storage'
import { createInitialGameState } from '@/engine/gameEngine'
import { createEmptyArchiveStats } from '@/store/retention'
import { createEmptyWorkspace } from '@/store/workspace'
import { restoreLegacyLearningState, type LegacyLearningState } from '@/store/migrations'
import { getStarterOutfit } from '@/data/outfitConfig'
import { createLearningStage } from '@/entities/stage'

export interface StoreBootstrapResult {
  initialState: Pick<AppState,
    'activeStudentId' | 'studentIndex' | 'workspaces' | 'profile' | 'student' |
    'currentGrade' | 'pretestScore' | 'pretestLayer' | 'learningStyle' | 'learnerType' |
    'completedTasks' | 'stars' | 'streak' | 'manualLayerOverride' | 'dynamicLayer' | 'currentStage' |
    'voiceEnabled' | 'llmEnabled' | 'llmApiKey' | 'llmBaseUrl' | 'llmModel' |
    'wrongQuestions' | 'assessmentSessions' | 'assessmentRecords' | 'rewardClaims' | 'initialProfileSessions' | 'vocabMastery' | 'weeklyPlanActive' |
    'weeklyPlanStartDate' | 'weeklyTaskDone' | 'taskCompletedAt' | 'currentPlan' | 'currentPlanKey' | 'formativeTaskSessions' |
    'attempts' | 'currentReport' | 'learningEvents' | 'archiveStats' | 'game' | 'sessionStats' |
    'lastXpEvent' | 'pendingAchievements' | 'lastLayerChangeReason' | 'currentOutfit' | 'unlockedOutfits' | 'ownedOutfits' |
    'aiTeacherResult' | 'aiTeacherFingerprint' | 'aiTeacherActive' | 'aiTeacherSession' | 'phaseAdvancement'>
  restoredLearningState: LegacyLearningState
}

function createDefaultStudent(restoredProfile: ReturnType<typeof migrateStudentLearningProfile>): StudentProfile {
  return {
    name: restoredProfile?.identity.name ?? '小探险家',
    grade: restoredProfile?.identity.grade ?? 'G3',
    avatar: restoredProfile?.identity.avatar ?? '🦊',
    layer: restoredProfile?.recommendedLevel ?? null,
    style: restoredProfile ? getPreferredLearningStyle(restoredProfile.preference) : null,
    totalStars: 0,
    streak: 1,
    completedUnits: [],
    completedLessons: [],
  }
}

export function createStoreBootstrap(): StoreBootstrapResult {
  const profileStorage = new LocalStorageService()
  const restoredProfile = migrateStudentLearningProfile(
    profileStorage.get<unknown>(STORAGE_KEYS.profile)?.data,
  )
  const restoredLearningState = restoreLegacyLearningState()
  const legacyStudentId = restoredProfile?.identity.id ?? 'local-demo-student'
  const createdAt = new Date().toISOString()

  const studentIndex: StudentAccount[] = restoredProfile ? [{
    id: legacyStudentId,
    name: restoredProfile.identity.name,
    grade: restoredProfile.identity.grade,
    avatar: restoredProfile.identity.avatar,
    hasCompletedPretest: true,
    createdAt,
    updatedAt: createdAt,
  }] : []

  const workspaces: Record<string, StudentWorkspace> = restoredProfile ? (() => {
    const style = getPreferredLearningStyle(restoredProfile.preference)
    const base = createEmptyWorkspace({
      name: restoredProfile.identity.name,
      grade: restoredProfile.identity.grade,
      avatar: restoredProfile.identity.avatar,
    })
    const learnerType = getPreferredLearnerType(restoredProfile.preference)
    const starterOutfit = getStarterOutfit(learnerType)
    return {
      [legacyStudentId]: {
        ...base,
        profile: restoredProfile,
        pretestScore: restoredProfile.pretestScore,
        pretestLayer: restoredProfile.recommendedLevel,
        currentStage: createLearningStage({ layer: restoredProfile.recommendedLevel, source: 'pretest' }),
        learningStyle: style,
        learnerType,
        student: { ...base.student, layer: restoredProfile.recommendedLevel, style },
        currentPlan: restoredLearningState.currentPlan ?? null,
        currentPlanKey: restoredLearningState.currentPlanKey ?? null,
        attempts: restoredLearningState.attempts ?? [],
        currentReport: restoredLearningState.currentReport ?? null,
        currentOutfit: starterOutfit,
        unlockedOutfits: starterOutfit === 'none' ? [] : [starterOutfit],
        ownedOutfits: starterOutfit === 'none' ? [] : [starterOutfit],
      },
    }
  })() : {}

  return {
    restoredLearningState,
    initialState: {
      activeStudentId: restoredProfile ? legacyStudentId : null,
      studentIndex,
      workspaces,
      profile: restoredProfile,
      student: createDefaultStudent(restoredProfile),
      currentGrade: 'all',
      pretestScore: restoredProfile?.pretestScore ?? null,
      pretestLayer: restoredProfile?.recommendedLevel ?? null,
      learningStyle: restoredProfile ? getPreferredLearningStyle(restoredProfile.preference) : null,
      learnerType: restoredProfile ? getPreferredLearnerType(restoredProfile.preference) : null,
      completedTasks: {},
      stars: 0,
      streak: 1,
      manualLayerOverride: null,
      dynamicLayer: null,
      currentStage: restoredProfile
        ? createLearningStage({ layer: restoredProfile.recommendedLevel, source: 'pretest' })
        : null,
      voiceEnabled: false,
      llmEnabled: false,
      llmApiKey: '',
      llmBaseUrl: '/api/ai',
      llmModel: 'glm-4-flash',
      wrongQuestions: [],
      assessmentSessions: [],
      assessmentRecords: [],
      rewardClaims: [],
      initialProfileSessions: [],
      vocabMastery: {},
      weeklyPlanActive: false,
      weeklyPlanStartDate: null,
      weeklyTaskDone: {},
      taskCompletedAt: {},
      currentPlan: restoredLearningState.currentPlan ?? null,
      currentPlanKey: restoredLearningState.currentPlanKey ?? null,
      formativeTaskSessions: [],
      attempts: restoredLearningState.attempts ?? [],
      currentReport: restoredLearningState.currentReport ?? null,
      learningEvents: [],
      archiveStats: createEmptyArchiveStats(),
      game: createInitialGameState(),
      sessionStats: null,
      lastXpEvent: null,
      pendingAchievements: [],
      lastLayerChangeReason: null,
      currentOutfit: restoredProfile ? getStarterOutfit(getPreferredLearnerType(restoredProfile.preference)) : 'none',
      unlockedOutfits: restoredProfile ? [getStarterOutfit(getPreferredLearnerType(restoredProfile.preference))].filter((outfit) => outfit !== 'none') : [],
      ownedOutfits: restoredProfile ? [getStarterOutfit(getPreferredLearnerType(restoredProfile.preference))].filter((outfit) => outfit !== 'none') : [],
      aiTeacherResult: null,
      aiTeacherFingerprint: '',
      aiTeacherActive: false,
      aiTeacherSession: null,
      phaseAdvancement: null,
    },
  }
}
